import { useState } from 'react'
import { Gavel } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { PermissionDenied } from '@/components/States'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { issueStrike, banUser } from '@/lib/adapters'
import { BAN_STR as S } from './strikesApi'

type Kind = 'strike' | 'ban'

/**
 * A8.05 — Qo'lda cheklov. JONLI: strayk `/admin/users/:id/strike` (backend
 * `0252c93`) yoki ban `/admin/users/:id/ban` (superadmin, serverда audit).
 * Sabab MAJBURIY (X2 daraja 2).
 */
export function ManualRestrictionScreen() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [kind, setKind] = useState<Kind>('strike')
  const [userId, setUserId] = useState('')
  const [severity, setSeverity] = useState(3)
  const [expiresDays, setExpiresDays] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  const canApply = can(PERMISSIONS.USERS_BAN)

  const uid = Number(userId)
  const uidValid = Number.isInteger(uid) && uid > 0
  const reasonValid = reason.trim().length > 0
  const days = Number(expiresDays)

  const submit = async () => {
    if (!uidValid) {
      toast(S.userIdRequired, { tone: 'warning' })
      return
    }
    if (!reasonValid) {
      toast(S.reasonRequired, { tone: 'warning' })
      return
    }
    setBusy(true)
    try {
      if (kind === 'strike') {
        await issueStrike(uid, severity, reason.trim(), undefined, days > 0 ? days : undefined)
        toast(S.issuedStrike, { tone: 'success' })
      } else {
        await banUser(uid, reason.trim())
        toast(S.issuedBan, { tone: 'success' })
      }
      setUserId('')
      setReason('')
      setExpiresDays('')
    } catch (e) {
      toast(S.failed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.manual} screenId="A8.05" description={S.manualNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-xl space-y-4 rounded-lg border border-border-default bg-surface-2 p-4">
          {/* Ograničenie turi */}
          <div>
            <div className="mb-1.5 text-sm font-medium text-secondary">{S.restrictionType}</div>
            <div className="flex gap-1.5">
              {(['strike', 'ban'] as Kind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={
                    'rounded-md border px-3 py-1.5 text-sm ' +
                    (kind === k
                      ? 'border-accent bg-accent-subtle text-accent'
                      : 'border-border-default bg-surface-1 text-secondary hover:text-primary')
                  }
                >
                  {k === 'strike' ? S.actStrike : S.actBan}
                </button>
              ))}
            </div>
          </div>

          {/* Foydalanuvchi ID */}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-secondary">{S.userId}</span>
            <Input
              mono
              inputMode="numeric"
              value={userId}
              onChange={(e) => setUserId(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="123456"
              aria-invalid={userId.length > 0 && !uidValid}
            />
          </label>

          {/* Strayk: tyazhest + srok */}
          {kind === 'strike' && (
            <div className="flex flex-wrap gap-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-secondary">{S.severity}</span>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(Number(e.target.value))}
                  className="h-8 rounded-md border border-border-default bg-surface-1 px-2.5 text-sm text-primary"
                >
                  {[1, 2, 3, 4, 5].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-secondary">{S.expiresDays}</span>
                <Input
                  mono
                  inputMode="numeric"
                  value={expiresDays}
                  onChange={(e) => setExpiresDays(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="0"
                  className="w-28"
                />
              </label>
            </div>
          )}

          {/* Prichina */}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-secondary">{S.reason}</span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              aria-invalid={reason.length > 0 && !reasonValid}
            />
          </label>

          <div className="flex items-center gap-3 border-t border-border-subtle pt-3">
            <Button
              variant={kind === 'ban' ? 'danger' : 'warning'}
              disabled={busy || !canApply || !uidValid || !reasonValid}
              onClick={submit}
              data-screen="A8.05"
            >
              <Gavel size={14} />
              {S.issue}
            </Button>
            {!canApply && <span className="text-xs text-tertiary">{PERMISSIONS.USERS_BAN}</span>}
          </div>
        </div>
      </div>
    </>
  )
}
